import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Search, ChevronRight, Loader2 } from "lucide-react";

export default function DashboardPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const { data } = await api.get("/reports");

    // normalize response
        if (Array.isArray(data)) {
          setReports(data);
        } else if (Array.isArray(data?.reports)) {
          setReports(data.reports);
        } else if (Array.isArray(data?.data)) {
          setReports(data.data);
        } else {
          console.error("Unexpected reports response:", data);
          setReports([]);
        }
      } catch (e) {
        console.error("Failed to load reports:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);
  const safeReports = Array.isArray(reports) ? reports : [];
  const grouped = safeReports.reduce((acc, r) => {
    const cat = r.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {});

  const handleClick = (report) => {
    if (report.key === "product_inquiry") {
      navigate("/product-inquiry");
    } else {
      navigate(`/reports/${report.key}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#A3A3A3] py-12" data-testid="dashboard-loading">
        <Loader2 size={20} className="animate-spin" />
        Loading reports...
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <h1
        className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-2"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Dashboard
      </h1>
      <p className="text-sm text-[#A3A3A3] mb-8">
        Select a report to run queries and export data
      </p>

      {Object.keys(grouped).length === 0 && (
        <p className="text-[#A3A3A3] text-sm" data-testid="no-reports-message">
          No reports available. Contact an admin to get access.
        </p>
      )}

      {Object.entries(grouped).map(([category, categoryReports]) => (
        <div key={category} className="mb-8">
          <h2
            className="text-xs font-semibold tracking-wider uppercase text-[#A3A3A3] mb-4"
            data-testid={`category-${category.toLowerCase()}`}
          >
            {category}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categoryReports.map((report) => (
              <Card
                key={report.key}
                onClick={() => handleClick(report)}
                className="bg-[#141414] border-[#262626] cursor-pointer hover:bg-[#1E1E1E] transition-colors duration-150 group"
                data-testid={`report-card-${report.key}`}
              >
                <CardHeader className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {report.key === "product_inquiry" ? (
                        <Search size={20} className="text-[#0066FF] shrink-0" />
                      ) : (
                        <FileText size={20} className="text-[#0066FF] shrink-0" />
                      )}
                      <div>
                        <CardTitle className="text-sm font-semibold text-white">
                          {report.name}
                        </CardTitle>
                        {report.description && (
                          <CardDescription className="text-xs text-[#A3A3A3] mt-1 line-clamp-2">
                            {report.description}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-[#A3A3A3] group-hover:text-white transition-colors shrink-0 mt-0.5"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    {report.system_report && (
                      <Badge variant="secondary" className="text-[10px] bg-[#1E1E1E] text-[#A3A3A3]">
                        System
                      </Badge>
                    )}
                    {report.export_excel && (
                      <Badge variant="outline" className="text-[10px] border-[#262626] text-[#A3A3A3]">
                        Excel
                      </Badge>
                    )}
                    {report.export_csv && (
                      <Badge variant="outline" className="text-[10px] border-[#262626] text-[#A3A3A3]">
                        CSV
                      </Badge>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
